import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  TableContainer,
  Paper,
  Grid,
  TextField,
  Box,
  Button,
  Tooltip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  CircularProgress,
  Typography,
  Checkbox,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { CustomPaper } from "components/Styles/globalStyles";
import Cookie from "js-cookie";
import { useNavigate } from "react-router-dom";
import URLs from "constants/urls";
import {
  inputStyle,
  StyledButton,
  StyledTypography,
  StyledSnackbar,
  StyledAlert,
} from "components/StyledComponents/styledComponents.js";
import ResponsiveTabs from "components/StyledComponents/styledComponents";
import {
  StyledTableContainer,
  StyledAgGridContainer,
} from "components/StyledComponents/styledComponents";
import { useMaterialUIController } from "context";
import dayjs from "dayjs";
import { ViewButton } from "components/StyledComponents/styledComponents.js";
import VisibilityIcon from "@mui/icons-material/Visibility";

export default function RunPayroll() {
  const [controller] = useMaterialUIController(); // Get context state
  const { user } = controller; // Destructure user
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [payPeriod, setPayPeriod] = useState({});
  const [employeeData, setEmployeeData] = useState([]);
  const [dsaData, setDsaData] = useState([]);
  const [gridData, setGridData] = useState([]);
  const jwt_token = Cookie.get("jwt_token");
  const navigate = useNavigate();
  const MInput = inputStyle();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [employeeConfirmPayRollData, setEmployeeConfirmPayRollData] = useState([]);
  const [payPeriodOptions, setPayPeriodOptions] = useState([]);
  const [paySlipsData, setPaySlipsData] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const fetchEmployeePayRollData = async () => {
    if (!payPeriod?.value) {
      setAlert("Please select a pay period", "error");
      return;
    }
    setLoading(true);
    const url = `${URLs.hrmsRunPayroll}/${payPeriod.value}/${user.companyId || 0}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch employee data");
      }
      const data = await response.json();
      setEmployeeData(data);
    } catch (error) {
      setAlert(error.message || "Failed to fetch employee data", "error");
      setEmployeeData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayRollIdData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${URLs.hrmsGetPayPeriods}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch pay periods");
      }
      const data = await response.json();
      const payPeriodOptionsFormatted = data.map((option) => ({
        label: option.payPeriodName,
        value: option.payPeriodId,
        activeStatus: "Y", // Setting default active status
      }));
      setPayPeriodOptions(payPeriodOptionsFormatted);
    } catch (error) {
      console.error("Failed to fetch pay period data:", error);
      setAlert("Failed to fetch pay periods", "error");
      setPayPeriodOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    fetchPayRollIdData();
  }, []);

  useEffect(() => {
    setSelectedEntryIds([]);
    setSelectedRows([]);
  }, [activeTab]);

  const fetchEmployeeConfirmPayRollData = async () => {
    if (!payPeriod?.value) {
      setAlert("Please select a pay period", "error");
      return;
    }
    setLoading(true);
    const url = `${URLs.hrmsConfirmPayroll}/${payPeriod.value}/${user.companyId || 0}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch confirm payroll data");
      }
      const data = await response.json();
      setEmployeeConfirmPayRollData(data);
    } catch (error) {
      setAlert(error.message || "Failed to fetch confirm payroll data", "error");
      setEmployeeConfirmPayRollData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDisplay = () => {
    if (payPeriod) {
      if (activeTab === 0) {
        fetchEmployeePayRollData();
      } else if (activeTab === 1) {
        fetchEmployeeConfirmPayRollData();
      } else if (activeTab === 2) {
        fetchPaySlipsData();
      }
    } else {
      setAlert("Select Pay Period", "success");
    }
  };

  const fetchPaySlipsData = async () => {
    if (!payPeriod?.value) {
      setAlert("Please select a pay period", "error");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `${URLs.hrmsViewPayslips}/${payPeriod.value}/${user.companyId || 0}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt_token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch payslips data");
      }

      const data = await response.json();
      setPaySlipsData(data);
    } catch (error) {
      console.error("Error fetching payslips:", error);
      setAlert("Failed to fetch payslips", "error");
      setPaySlipsData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "payslip.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleViewPayslip = async (employeeRecordId) => {
    if (!payPeriod?.value) {
      setAlert("Please select a pay period", "error");
      return;
    }
    try {
      setLoading(true);
      const response = await fetch(
        `${URLs.hrmsPayslipPdf}/${payPeriod.value}/${employeeRecordId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt_token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to generate payslip");
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error("Generated PDF is empty");
      }

      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setDialogOpen(true);
      setAlert("Payslip generated successfully", "success");
    } catch (error) {
      console.error("Error generating payslip:", error);
      setAlert(error.message || "Failed to generate payslip", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxClick = (entryId) => {
    setSelectedEntryIds((prev) => {
      const updatedIds = prev.includes(entryId)
        ? prev.filter((id) => id !== entryId)
        : [...prev, entryId];

      // Update selectedRows based on selectedEntryIds
      const updatedRows = employeeData.filter((row) => updatedIds.includes(row.entryId));
      setSelectedRows(updatedRows);

      return updatedIds;
    });
  };
  const handleSelectAll = () => {
    if (selectedEntryIds.length === employeeData.length) {
      setSelectedEntryIds([]); // Deselect all
      setSelectedRows([]);
    } else {
      setSelectedEntryIds(employeeData.map((row) => row.employeeId));
      setSelectedRows(employeeData); // Set all rows as selected
    }
  };
  const HeaderCheckbox = () => (
    <Checkbox
      checked={selectedEntryIds.length === employeeData.length && employeeData.length > 0}
      indeterminate={selectedEntryIds.length > 0 && selectedEntryIds.length < employeeData.length}
      onChange={handleSelectAll}
    />
  );
  const columns = [
    {
      field: "addToPayRoll",
      headerName: "Select All",
      headerComponent: HeaderCheckbox,
      align: "center",
      width: 50,
      cellRenderer: (params) => (
        <Checkbox
          checked={selectedEntryIds.includes(params.data.employeeId)}
          onChange={() => handleCheckboxClick(params.data.employeeId)}
        />
      ),
    },
    { headerName: "Employee ID", field: "employeeId", sortable: true, filter: true, flex: 1 },
    { headerName: "Employee Name", field: "employeeName", sortable: true, filter: true, flex: 1 },
    { headerName: "Working Days", field: "noOfWorkingDays", sortable: true, filter: true, flex: 1 },
    {
      headerName: "Attendance",
      field: "noOfAttendanceDays",
      sortable: true,
      filter: true,
      flex: 1,
    },
    { headerName: "Leaves", field: "noOfLeaves", sortable: true, filter: true, flex: 1 },
    { headerName: "LOP", field: "lossOfPayDays", sortable: true, filter: true, flex: 1 },
  ];
  const ConfirmPayrollcolumns = [
    {
      field: "addToPayRoll",
      headerName: "Select All",
      headerComponent: HeaderCheckbox,
      align: "center",
      width: 50,
      cellRenderer: (params) => (
        <Checkbox
          checked={selectedEntryIds.includes(params.data.employeeId)}
          onChange={() => handleCheckboxClick(params.data.employeeId)}
        />
      ),
    },
    { headerName: "Employee ID", field: "employeeId", sortable: true, filter: true, flex: 1 },
    { headerName: "Employee Name", field: "employeeName", sortable: true, filter: true, flex: 1 },
    { headerName: "Working Days", field: "noOfWorkingDays", sortable: true, filter: true, flex: 1 },
    {
      headerName: "Attendance",
      field: "noOfAttendanceDays",
      sortable: true,
      filter: true,
      flex: 1,
    },
    { headerName: "Leaves", field: "noOfLeaves", sortable: true, filter: true, flex: 1 },
    { headerName: "LOP", field: "lossOfPayDays", sortable: true, filter: true, flex: 1 },
    { headerName: "Deduction", field: "totalDeductions", sortable: true, filter: true, flex: 1 },
    { headerName: "Net Pay", field: "netPay", sortable: true, filter: true, flex: 1 },
  ];
  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  const tabs = [{ label: "Run PayRoll" }, { label: "Confirm PayRoll" }, { label: "Pay Slips" }];

  const runPayRollForEmployees = async () => {
    const selectedEmployees = [...selectedEntryIds];
    if (selectedEmployees.length === 0) {
      setAlert("Please select employees for payroll", "error");
      return;
    }
    if (!payPeriod?.value) {
      setAlert("Please select Pay Period", "error");
      return;
    }

    setLoading(true);
    try {
      const url =
        activeTab === 0
          ? `${URLs.hrmsPostRunPayroll}?payPeriodId=${payPeriod.value}`
          : `${URLs.hrmsPostConfirmPayroll}?payPeriodId=${payPeriod.value}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        body: JSON.stringify(selectedEmployees),
      });

      if (!response.ok) {
        throw new Error(`Failed to process payroll. Status: ${response.status}`);
      }

      const responseData = await response.text();
      setSelectedEntryIds([]); // Clear selections
      setSelectedRows([]); // Clear selected rows
      setAlert(
        activeTab === 0 ? "Payroll processed successfully" : "Payroll confirmed successfully",
        "success"
      );

      // Refresh the data based on active tab
      if (activeTab === 0) {
        fetchEmployeePayRollData();
      } else {
        fetchEmployeeConfirmPayRollData();
      }
    } catch (error) {
      console.error("Payroll processing error:", error);
      setAlert(error.message || "Failed to process payroll", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
          overflow: "auto",
          flexGrow: 1,
          boxShadow: "none",
          border: "none",
          height: "100%",
          pt: 2,
          backgroundColor: "transparent",
        }}
      >
        <ResponsiveTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={handleChange}
          labelColor="secondary"
          bgColor="#e0f7fa"
        />
        <Dialog
          open={loading}
          onClose={() => setLoading(false)}
          aria-labelledby="loading-dialog-title"
        >
          <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <CircularProgress color="primary" />
            <Typography variant="h6" style={{ marginTop: "20px" }}>
              Loading, please wait...
            </Typography>
          </DialogContent>
        </Dialog>
        <StyledSnackbar
          open={open}
          autoHideDuration={5000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          severity={alertType}
        >
          <StyledAlert onClose={handleClose} severity={alertType}>
            {message}
          </StyledAlert>
        </StyledSnackbar>
        {activeTab === 0 && (
          <Box
            sx={{
              overflowX: "auto",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={6} md={4} lg={2}>
                  <Autocomplete
                    options={payPeriodOptions}
                    getOptionLabel={(option) => option.label}
                    value={
                      payPeriodOptions.find((option) => option.value === payPeriod.value) || null
                    }
                    onChange={(event, value) => setPayPeriod(value ? value : "")}
                    PaperComponent={CustomPaper}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Pay Period"
                        required
                        sx={{
                          height: 43,
                          ...MInput.b,
                          "& .MuiInputBase-root": {
                            height: "100%",
                            minHeight: 43,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            color: MInput.a, // Ensure this applies to the label
                            "&.Mui-focused": {
                              color: "red", // Focused label color
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <StyledButton onClick={handleDisplay}>Display</StyledButton>
                </Grid>
              </Grid>
            </Box>
            <StyledAgGridContainer
              component={Paper}
              className="ag-theme-alpine"
              sx={{
                flexGrow: 1,
                backgroundColor: "transparent",
                boxShadow: "none",
                border: "none",
              }}
            >
              <AgGridReact
                rowData={employeeData}
                columnDefs={columns}
                pagination
                paginationPageSize={11}
              />
            </StyledAgGridContainer>

            <Box sx={{ display: "flex", justifyContent: "flex-end", m: 2 }}>
              <StyledButton
                disabled={payPeriod.activeStatus != "Y"}
                onClick={runPayRollForEmployees}
              >
                Run Payroll
              </StyledButton>
            </Box>
          </Box>
        )}
        {activeTab === 1 && (
          <Box
            sx={{
              overflowX: "auto",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={6} md={4} lg={2}>
                  <Autocomplete
                    options={payPeriodOptions}
                    getOptionLabel={(option) => option.label}
                    value={
                      payPeriodOptions.find((option) => option.value === payPeriod.value) || null
                    }
                    onChange={(event, value) => setPayPeriod(value ? value : "")}
                    PaperComponent={CustomPaper}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Pay Period"
                        required
                        sx={{
                          height: 43,
                          ...MInput.b,
                          "& .MuiInputBase-root": {
                            height: "100%",
                            minHeight: 43,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            color: MInput.a, // Ensure this applies to the label
                            "&.Mui-focused": {
                              color: "red", // Focused label color
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <StyledButton onClick={handleDisplay}>Display</StyledButton>
                </Grid>
              </Grid>
            </Box>
            <StyledAgGridContainer
              component={Paper}
              className="ag-theme-alpine"
              sx={{
                flexGrow: 1,
                backgroundColor: "transparent",
                boxShadow: "none",
                border: "none",
              }}
            >
              <AgGridReact
                rowData={employeeConfirmPayRollData}
                columnDefs={ConfirmPayrollcolumns}
                pagination
                paginationPageSize={11}
              />
            </StyledAgGridContainer>

            <Box sx={{ display: "flex", justifyContent: "flex-end", m: 2 }}>
              <StyledButton
                // disabled={payPeriod.activeStatus != "Y"}
                onClick={runPayRollForEmployees}
              >
                Confirm Payroll
              </StyledButton>
            </Box>
          </Box>
        )}
        {activeTab === 2 && (
          <Box
            sx={{
              overflowX: "auto",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={6} md={4} lg={2}>
                  <Autocomplete
                    options={payPeriodOptions}
                    getOptionLabel={(option) => option.label}
                    value={
                      payPeriodOptions.find((option) => option.value === payPeriod.value) || null
                    }
                    onChange={(event, value) => setPayPeriod(value ? value : "")}
                    PaperComponent={CustomPaper}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Pay Period"
                        required
                        sx={{
                          height: 43,
                          ...MInput.b,
                          "& .MuiInputBase-root": {
                            height: "100%",
                            minHeight: 43,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            color: MInput.a,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <StyledButton onClick={handleDisplay}>Display</StyledButton>
                </Grid>
              </Grid>
            </Box>
            <StyledAgGridContainer
              component={Paper}
              className="ag-theme-alpine"
              sx={{
                flexGrow: 1,
                backgroundColor: "transparent",
                boxShadow: "none",
                border: "none",
              }}
            >
              <AgGridReact
                rowData={paySlipsData}
                columnDefs={[
                  {
                    field: "employeeName",
                    headerName: "Employee Name",
                    filter: true,
                    sortable: true,
                    flex: 1.2,
                  },
                  {
                    field: "noOfWorkingDays",
                    headerName: "Working Days",
                    filter: true,
                    sortable: true,
                    flex: 1,
                    cellStyle: { textAlign: "center" },
                  },
                  {
                    field: "noOfAttendanceDays",
                    headerName: "Attendance",
                    filter: true,
                    sortable: true,
                    flex: 1,
                    cellStyle: { textAlign: "center" },
                  },
                  {
                    field: "noOfLeaves",
                    headerName: "Leaves",
                    filter: true,
                    sortable: true,
                    flex: 1,
                    cellStyle: { textAlign: "center" },
                  },
                  {
                    field: "lossOfPayDays",
                    headerName: "LOP",
                    filter: true,
                    sortable: true,
                    flex: 1,
                    cellStyle: { textAlign: "center" },
                  },
                  {
                    field: "totalAdditions",
                    headerName: "Addition",
                    filter: true,
                    sortable: true,
                    flex: 1,
                    cellStyle: { textAlign: "right" },
                    valueFormatter: (params) =>
                      `₹ ${params.value?.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`,
                  },
                  {
                    field: "totalDeductions",
                    headerName: "Deduction",
                    filter: true,
                    sortable: true,
                    flex: 1,
                    cellStyle: { textAlign: "right" },
                    valueFormatter: (params) =>
                      `₹ ${params.value?.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`,
                  },
                  {
                    field: "netPay",
                    headerName: "Net Pay",
                    filter: true,
                    sortable: true,
                    flex: 1,
                    cellStyle: { textAlign: "right", fontWeight: "bold" },
                    valueFormatter: (params) =>
                      `₹ ${params.value?.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`,
                  },
                  {
                    headerName: "Actions",
                    field: "actions",
                    sortable: false,
                    filter: false,
                    flex: 0.8,
                    cellRenderer: (params) => (
                      <ViewButton
                        onClick={() => handleViewPayslip(params.data.employeeRecordId)}
                        sx={{ minWidth: "40px", padding: "4px" }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </ViewButton>
                    ),
                  },
                ]}
                pagination
                paginationPageSize={11}
              />
            </StyledAgGridContainer>
          </Box>
        )}
      </Box>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogContent>
          {pdfUrl && (
            <div style={{ height: "80vh", display: "flex", flexDirection: "column" }}>
              <iframe
                src={pdfUrl}
                style={{ width: "100%", height: "100%", border: "none" }}
                title="Payslip Preview"
              />
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                <StyledButton onClick={handleDownload} variant="contained">
                  Download
                </StyledButton>
              </Box>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
